import { useCallback, useEffect, useState } from 'react'

import { useGetConnectionsWebPage } from 'commons/hooks/bank/connections'
import { TableSortingOrder } from 'ui/components'
import { useTableState } from 'ui/components/Table'

import { ConnectionsListFormPayload, ConnectionsListWebPage } from '../types'

const CONNECTIONS_LIMIT = 10
const CONNECTIONS_OFFSET = 0

export const useConnectionsList = () => {
  const [webPageData, setWebPageData] = useState<ConnectionsListWebPage>()
  const [webPageFilter, setWebPageFilter] = useState<ConnectionsListFormPayload>({
    connectionsLimit: CONNECTIONS_LIMIT,
    connectionsOffset: CONNECTIONS_OFFSET
  })

  const onBeforeFilterChange = ({ value, errors }) => {
    const newFilter = { ...webPageFilter }
    newFilter.isConnected = value.isConnected
    newFilter.connectionType = value.connectionType
    newFilter.connectionsOffset = CONNECTIONS_OFFSET
    newFilter.connectionsLimit = CONNECTIONS_LIMIT
    setWebPageFilter(newFilter)
    getListWebPage(newFilter)

    return {
      value: {
        ...value
      },
      errors
    }
  }

  const tableState = useTableState<ConnectionsListFormPayload>({
    onBeforeFilterChange,
    order: [
      {
        field: 'companyName',
        direction: 'ASC'
      }
    ],
    size: CONNECTIONS_LIMIT,
    page: 1,
    filter: {
      connectionsSearchQuery: '',
      connectionType: null,
      isConnected: null
    }
  })

  const { routine: getListWebPage, isLoading } = useGetConnectionsWebPage({
    onSuccess: async (_, result) => {
      setWebPageData(result)
    }
  })

  const onSetWebPageOrder = (order: TableSortingOrder) => {
    const newFilter = { ...webPageFilter }
    newFilter.sortDesc = order.direction === 'DESC'
    tableState.onOrderChange(order)
    setWebPageFilter(newFilter)
    getListWebPage(newFilter)
  }

  const onSetWebPagePagination = useCallback(
    (connectionsPage: number, connectionsLimit: number) => {
      const newFilter = { ...webPageFilter }
      const connectionsOffset = (connectionsPage - 1) * connectionsLimit
      tableState.onPageChange(connectionsPage, connectionsLimit)
      newFilter.connectionsOffset = connectionsOffset
      newFilter.connectionsLimit = connectionsLimit
      setWebPageFilter(newFilter)
      getListWebPage(newFilter)
    },
    [tableState, webPageFilter, getListWebPage]
  )

  const onSetWebPageQuery = (searchWord: string) => {
    const newFilter = { ...webPageFilter }
    newFilter.connectionsSearchQuery = searchWord
    setWebPageFilter(newFilter)
    tableState.filterForm.set({ value: { ...tableState.filterForm.config.value, connectionsSearchQuery: searchWord } })
    getListWebPage(newFilter)
  }

  const onClear = async () => {
    tableState.onFilterClear()
    tableState.filterForm.set({ reset: true })

    const newFilter = {
      connectionsLimit: CONNECTIONS_LIMIT,
      connectionsOffset: CONNECTIONS_OFFSET
    }

    setWebPageFilter(newFilter)
    getListWebPage(newFilter)
  }

  useEffect(() => {
    getListWebPage(webPageFilter)
  }, [])

  const [isSetupKeyDialogOpen, setSetupKeyDialogOpen] = useState(false)

  const openSetupKeyDialog = () => setSetupKeyDialogOpen(true)
  const closeSetupKeyDialog = () => setSetupKeyDialogOpen(false)

  const reloadData = () => {
    getListWebPage(webPageFilter)
  }

  return {
    tableState,
    webPageData,
    isLoading,
    onSetWebPagePagination,
    onSetWebPageQuery,
    onSetWebPageOrder,
    onClear,
    isSetupKeyDialogOpen,
    openSetupKeyDialog,
    closeSetupKeyDialog,
    reloadData
  }
}
